import { type RouteObject } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { Skeleton, Card, CardBody } from '@heroui/react';
import AuthGuard from '@/presentation/guards/AuthGuard';
import GuestGuard from '@/presentation/guards/GuestGuard';

// Layouts
const MainLayout = lazy(() => import('@/presentation/layouts/MainLayout'));
const AuthLayout = lazy(() => import('@/presentation/layouts/AuthLayout'));

// Auth pages
const LoginPage = lazy(() => import('@/presentation/pages/auth/LoginPage'));
const ForgotPasswordPage = lazy(() => import('@/presentation/pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('@/presentation/pages/auth/ResetPasswordPage'));

// Profile
const ProfilePage = lazy(() => import('@/presentation/pages/auth/ProfilePage'));

// Dashboard
const DashboardPage = lazy(() => import('@/presentation/pages/dashboard/DashboardPage'));

// Projects
const ProjectListPage = lazy(() => import('@/presentation/pages/projects/ProjectListPage'));
const ProjectCreatePage = lazy(() => import('@/presentation/pages/projects/ProjectCreatePage'));
const ProjectDetailPage = lazy(() => import('@/presentation/pages/projects/ProjectDetailPage'));
const ProjectProfileDashboard = lazy(() => import('@/presentation/pages/projects/ProjectProfileDashboard'));

// Risks
const RiskRegisterListPage = lazy(() => import('@/presentation/pages/risks/RiskRegisterListPage'));
const RiskRegisterDetailPage = lazy(() => import('@/presentation/pages/risks/RiskRegisterDetailPage'));
const RiskRegisterFormPage = lazy(() => import('@/presentation/pages/risks/RiskRegisterFormPage'));
const MitigationListPage = lazy(() => import('@/presentation/pages/risks/MitigationListPage'));
const MitigationFormPage = lazy(() => import('@/presentation/pages/risks/MitigationFormPage'));

// Monte Carlo
const MonteCarloPage = lazy(() => import('@/presentation/pages/monte-carlo/MonteCarloPage'));

// Assessments
const ControlAssessmentPage = lazy(() => import('@/presentation/pages/assessments/ControlAssessmentPage'));
const TreatmentAssessmentPage = lazy(() => import('@/presentation/pages/assessments/TreatmentAssessmentPage'));

// Approvals
const RiskApprovalPage = lazy(() => import('@/presentation/pages/approvals/RiskApprovalPage'));

// Monitoring
const KRIListPage = lazy(() => import('@/presentation/pages/monitoring/KRIListPage'));
const KRIMonitoringPage = lazy(() => import('@/presentation/pages/monitoring/KRIMonitoringPage'));
const EVMListPage = lazy(() => import('@/presentation/pages/monitoring/EVMListPage'));
const EVMFormPage = lazy(() => import('@/presentation/pages/monitoring/EVMFormPage'));
const EVMDetailPage = lazy(() => import('@/presentation/pages/monitoring/EVMDetailPage'));

// Portfolio
const PortfolioDashboardPage = lazy(() => import('@/presentation/pages/portfolio/PortfolioDashboardPage'));
const ProjectComparisonPage = lazy(() => import('@/presentation/pages/portfolio/ProjectComparisonPage'));

// Reports
const RiskReportPage = lazy(() => import('@/presentation/pages/reports/RiskReportPage'));
const PortfolioReportPage = lazy(() => import('@/presentation/pages/reports/PortfolioReportPage'));
const EVMReportPage = lazy(() => import('@/presentation/pages/reports/EVMReportPage'));
const KRIReportPage = lazy(() => import('@/presentation/pages/reports/KRIReportPage'));
const MitigationReportPage = lazy(() => import('@/presentation/pages/reports/MitigationReportPage'));
const ScheduledReportsPage = lazy(() => import('@/presentation/pages/reports/ScheduledReportsPage'));
const DataExportPage = lazy(() => import('@/presentation/pages/reports/DataExportPage'));

// Admin
const UserManagementPage = lazy(() => import('@/presentation/pages/admin/UserManagementPage'));
const RolesPermissionsPage = lazy(() => import('@/presentation/pages/admin/RolesPermissionsPage'));
const MasterCompaniesPage = lazy(() => import('@/presentation/pages/admin/MasterCompaniesPage'));
const MasterTaxonomyPage = lazy(() => import('@/presentation/pages/admin/MasterTaxonomyPage'));
const MasterCriteriaPage = lazy(() => import('@/presentation/pages/admin/MasterCriteriaPage'));
const MasterMatrixPage = lazy(() => import('@/presentation/pages/admin/MasterMatrixPage'));
const MasterReferencesPage = lazy(() => import('@/presentation/pages/admin/MasterReferencesPage'));
const ModuleManagementPage = lazy(() => import('@/presentation/pages/admin/ModuleManagementPage'));
const AuditLogPage = lazy(() => import('@/presentation/pages/admin/AuditLogPage'));
const SystemSettingsPage = lazy(() => import('@/presentation/pages/admin/SystemSettingsPage'));

// System
const HealthCheckPage = lazy(() => import('@/presentation/pages/system/HealthCheckPage'));
const ErrorLogPage = lazy(() => import('@/presentation/pages/system/ErrorLogPage'));
const APMPage = lazy(() => import('@/presentation/pages/system/APMPage'));
const DatabaseBackupPage = lazy(() => import('@/presentation/pages/system/DatabaseBackupPage'));
const APIManagementPage = lazy(() => import('@/presentation/pages/system/APIManagementPage'));

// Notifications
const NotificationPage = lazy(() => import('@/presentation/pages/notifications/NotificationPage'));

function LoadingFallback() {
  return (
    <div className="flex flex-col gap-4 p-6">
      {/* Header skeleton */}
      <div className="flex items-center justify-between">
        <Skeleton className="h-8 w-48 rounded-lg" />
        <Skeleton className="h-10 w-32 rounded-lg" />
      </div>
      {/* Content skeleton */}
      <Card shadow="sm">
        <CardBody className="gap-3 p-4">
          <Skeleton className="h-10 w-full rounded-lg" />
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-12 w-full rounded-lg" />
          ))}
        </CardBody>
      </Card>
      <div className="flex items-center justify-between">
        <Skeleton className="h-8 w-40 rounded-lg" />
        <Skeleton className="h-8 w-48 rounded-lg" />
      </div>
    </div>
  );
}

function withSuspense(Component: React.LazyExoticComponent<() => JSX.Element>) {
  return (
    <Suspense fallback={<LoadingFallback />}>
      <Component />
    </Suspense>
  );
}

export const routes: RouteObject[] = [
  // Auth routes (guest only — redirect to / if already logged in)
  {
    element: (
      <GuestGuard>
        <Suspense fallback={<LoadingFallback />}>
          <AuthLayout />
        </Suspense>
      </GuestGuard>
    ),
    children: [
      { path: '/login', element: withSuspense(LoginPage) },
      { path: '/forgot-password', element: withSuspense(ForgotPasswordPage) },
      { path: '/reset-password', element: withSuspense(ResetPasswordPage) },
    ],
  },

  // Protected routes (with AuthGuard + MainLayout)
  {
    element: (
      <AuthGuard>
        <Suspense fallback={<LoadingFallback />}>
          <MainLayout />
        </Suspense>
      </AuthGuard>
    ),
    children: [
      // Dashboard
      { path: '/', element: withSuspense(DashboardPage) },

      // Profile
      { path: '/profile', element: withSuspense(ProfilePage) },

      // Projects
      { path: '/projects', element: withSuspense(ProjectListPage) },
      { path: '/projects/create', element: withSuspense(ProjectCreatePage) },
      { path: '/projects/:id', element: withSuspense(ProjectDetailPage) },
      { path: '/projects/:id/edit', element: withSuspense(ProjectCreatePage) },
      { path: '/projects/:id/dashboard', element: withSuspense(ProjectProfileDashboard) },

      // Risk Register
      { path: '/risks', element: withSuspense(RiskRegisterListPage) },
      { path: '/risks/create', element: withSuspense(RiskRegisterFormPage) },
      { path: '/risks/:id', element: withSuspense(RiskRegisterDetailPage) },
      { path: '/risks/:id/edit', element: withSuspense(RiskRegisterFormPage) },
      { path: '/risks/:id/mitigations', element: withSuspense(MitigationFormPage) },

      // Perlakuan Risiko (Mitigasi)
      { path: '/mitigations', element: withSuspense(MitigationListPage) },

      // Penilaian Kontrol & Treatment
      { path: '/control-assessment', element: withSuspense(ControlAssessmentPage) },
      { path: '/treatment-assessment', element: withSuspense(TreatmentAssessmentPage) },

      // Simulasi Monte Carlo
      { path: '/monte-carlo', element: withSuspense(MonteCarloPage) },

      // Persetujuan Risiko
      { path: '/approvals', element: withSuspense(RiskApprovalPage) },

      // Monitoring - KRI
      { path: '/monitoring/kri', element: withSuspense(KRIListPage) },
      { path: '/monitoring/kri/:id', element: withSuspense(KRIMonitoringPage) },

      // Monitoring - EVM
      { path: '/monitoring/evm', element: withSuspense(EVMListPage) },
      { path: '/monitoring/evm/create', element: withSuspense(EVMFormPage) },
      { path: '/monitoring/evm/:id', element: withSuspense(EVMDetailPage) },
      { path: '/monitoring/evm/:id/edit', element: withSuspense(EVMFormPage) },

      // Portfolio
      { path: '/portfolio', element: withSuspense(PortfolioDashboardPage) },
      { path: '/comparison', element: withSuspense(ProjectComparisonPage) },

      // Reports
      { path: '/reports/risk', element: withSuspense(RiskReportPage) },
      { path: '/reports/portfolio', element: withSuspense(PortfolioReportPage) },
      { path: '/reports/evm', element: withSuspense(EVMReportPage) },
      { path: '/reports/kri', element: withSuspense(KRIReportPage) },
      { path: '/reports/mitigation', element: withSuspense(MitigationReportPage) },
      { path: '/reports/scheduled', element: withSuspense(ScheduledReportsPage) },
      { path: '/reports/export', element: withSuspense(DataExportPage) },

      // Admin
      { path: '/admin/users', element: withSuspense(UserManagementPage) },
      { path: '/admin/roles', element: withSuspense(RolesPermissionsPage) },
      { path: '/admin/modules', element: withSuspense(ModuleManagementPage) },
      { path: '/admin/companies', element: withSuspense(MasterCompaniesPage) },
      { path: '/admin/taxonomy', element: withSuspense(MasterTaxonomyPage) },
      { path: '/admin/criteria', element: withSuspense(MasterCriteriaPage) },
      { path: '/admin/matrix', element: withSuspense(MasterMatrixPage) },
      { path: '/admin/references', element: withSuspense(MasterReferencesPage) },
      { path: '/admin/audit-log', element: withSuspense(AuditLogPage) },
      { path: '/admin/settings', element: withSuspense(SystemSettingsPage) },

      // System
      { path: '/system/health', element: withSuspense(HealthCheckPage) },
      { path: '/system/errors', element: withSuspense(ErrorLogPage) },
      { path: '/system/apm', element: withSuspense(APMPage) },
      { path: '/system/backup', element: withSuspense(DatabaseBackupPage) },
      { path: '/system/api', element: withSuspense(APIManagementPage) },

      // Notifications
      { path: '/notifications', element: withSuspense(NotificationPage) },
    ],
  },
];
